import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IPersonFilter } from 'app/models/filters/person-filter';
import { IPerson } from 'app/models/people/person';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface IStaffAffiliationRequestDialogData {
  title?: string;
  existingPersonIds?: number[];
}

interface IEmailCandidate {
  email: string;
  typePriority: number;
  recency: number;
}

@Component({
  selector: 'app-staff-affiliation-request-dialog',
  templateUrl: './staff-affiliation-request-dialog.component.html',
  styleUrls: ['./staff-affiliation-request-dialog.component.scss']
})
export class StaffAffiliationRequestDialogComponent implements OnInit {
  public personForm: FormGroup;
  public matchingPeople: IPerson[] = [];
  public searchCompleted = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IStaffAffiliationRequestDialogData,
    private readonly dialogRef: MatDialogRef<StaffAffiliationRequestDialogComponent>,
    private readonly personService: PersonService,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.personForm = new FormGroup({
      selectedPerson: new FormControl(null, [Validators.required, this.existingPersonValidator()]),
    });

    this.selectedPersonControl?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe((value) => {
      if (typeof value !== 'string') {
        this.matchingPeople = [];
        this.searchCompleted = false;
        return;
      }

      this.searchExistingPeople(value);
    });
  }

  get selectedPersonControl(): AbstractControl | null {
    return this.personForm.get('selectedPerson');
  }

  get selectedPerson(): IPerson | null {
    const value = this.selectedPersonControl?.value;
    return value && typeof value !== 'string' ? value : null;
  }

  get selectedPersonErrorMessage(): string {
    const value = this.selectedPersonControl?.value;
    return typeof value === 'string' && value.trim().length < 3
      ? 'Type at least 3 characters, then choose a person from the list.'
      : 'Choose an existing person from the search results.';
  }

  displayPerson(value: IPerson | string | null): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    const fullName = this.getPersonFullName(value);
    const preferredEmail = this.getPreferredEmail(value);
    return preferredEmail ? fullName + ' - ' + preferredEmail : fullName;
  }

  showSelectedPersonRequiredError(): boolean {
    return !!this.selectedPersonControl && this.selectedPersonControl.hasError('required') && this.selectedPersonControl.touched;
  }

  showSelectedPersonLookupError(): boolean {
    return !!this.selectedPersonControl && this.selectedPersonControl.hasError('personSelectionRequired') && this.selectedPersonControl.touched;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    if (this.personForm.invalid) {
      this.personForm.markAllAsTouched();
      return;
    }

    const selectedPerson = this.selectedPerson;
    if (!selectedPerson) {
      this.selectedPersonControl?.markAsTouched();
      return;
    }

    this.dialogRef.close(selectedPerson);
  }

  private existingPersonValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      return value && typeof value !== 'string' && value.id ? null : { personSelectionRequired: true };
    };
  }

  private searchExistingPeople(rawValue: string): void {
    const searchTerm = rawValue.trim();

    if (searchTerm.length < 3) {
      this.matchingPeople = [];
      this.searchCompleted = false;
      return;
    }

    const filter: IPersonFilter = {
      filterString: searchTerm,
      filterStatus: '',
      filterAffiliation: [],
      expirationStartDate: '',
      expirationEndDate: '',
      filterDiscipline: [],
    };

    const existingPersonIds = new Set((this.data?.existingPersonIds || []).filter((personId) => personId > 0));

    this.personService.getPeopleFiltered(filter).subscribe({
      next: (people) => {
        this.matchingPeople = (people || []).filter((person) => !existingPersonIds.has(person.id));
        this.searchCompleted = true;
      },
      error: () => {
        this.matchingPeople = [];
        this.searchCompleted = true;
        this.toastr.error('Unable to search for people.');
      }
    });
  }

  private getPersonFullName(person: IPerson): string {
    const parts = [person.firstName, person.middleName, person.lastName]
      .map((namePart) => (namePart || '').trim())
      .filter((namePart) => namePart.length > 0);

    return parts.join(' ');
  }

  private getPreferredEmail(person: IPerson): string | null {
    const directEmail = this.normalizeEmail((person as any)?.preferredEmail || (person as any)?.email);
    if (directEmail) {
      return directEmail;
    }

    const contactInformations = (person as any)?.contactInformations;
    if (!Array.isArray(contactInformations) || !contactInformations.length) {
      return null;
    }

    const emailCandidates = contactInformations
      .map((contactInformation) => this.toEmailCandidate(contactInformation))
      .filter((candidate): candidate is IEmailCandidate => candidate !== null)
      .sort((firstCandidate, secondCandidate) => {
        if (secondCandidate.typePriority !== firstCandidate.typePriority) {
          return secondCandidate.typePriority - firstCandidate.typePriority;
        }

        if (secondCandidate.recency !== firstCandidate.recency) {
          return secondCandidate.recency - firstCandidate.recency;
        }

        return firstCandidate.email.localeCompare(secondCandidate.email);
      });

    return emailCandidates.length ? emailCandidates[0].email : null;
  }

  private toEmailCandidate(contactInformationContainer: any): IEmailCandidate | null {
    const contactInformation = contactInformationContainer?.contactInformation || contactInformationContainer;
    const email = this.normalizeEmail(
      contactInformation?.email
      || contactInformation?.Email
      || contactInformationContainer?.email
      || contactInformationContainer?.Email
    );

    if (!email) {
      return null;
    }

    const contactType = (
      contactInformation?.contactType
      || contactInformation?.ContactType
      || contactInformationContainer?.contactType
      || contactInformationContainer?.ContactType
      || ''
    ).toString();

    const recency = Math.max(
      this.parseDateEpoch(contactInformation?.lastUpdated || contactInformation?.LastUpdated),
      this.parseDateEpoch(contactInformation?.createDate || contactInformation?.CreateDate),
      this.parseDateEpoch(contactInformationContainer?.lastUpdated || contactInformationContainer?.LastUpdated),
      this.parseDateEpoch(contactInformationContainer?.createDate || contactInformationContainer?.CreateDate),
    );

    return {
      email,
      typePriority: this.getEmailTypePriority(contactType),
      recency,
    };
  }

  private normalizeEmail(value: string | null | undefined): string | null {
    const normalizedValue = (value || '').trim();
    return normalizedValue.length ? normalizedValue : null;
  }

  private parseDateEpoch(value: unknown): number {
    if (!value) {
      return 0;
    }

    const parsedDate = new Date(value as string).getTime();
    return Number.isNaN(parsedDate) ? 0 : parsedDate;
  }

  private getEmailTypePriority(contactType: string): number {
    const normalizedType = (contactType || '').trim().toLowerCase();

    if (!normalizedType.length) {
      return 0;
    }

    if (normalizedType === 'work' || normalizedType === 'business' || normalizedType === 'primary' || normalizedType === 'main') {
      return 3;
    }

    if (normalizedType === 'home' || normalizedType === 'personal') {
      return 2;
    }

    return 1;
  }
}
